import { findEntityMatches } from "../../shared/visualizeEntities.js";
import { isNotCommonWord } from "../../shared/storyBibleEntityNames.js";

/** Mention markup: #[label](type::id) or legacy @[label](type::id) */
const MENTION_MARKUP_RE = /(?:@|#)\[[^\]]*\]\([^)]*\)/g;

export type DocumentCandidate = { id: string; display: string };

function chapterSortKey(title: string): number | null {
  const trimmed = (title || "").trim();
  const match = /^chapter\s+(\d+)\b/i.exec(trimmed);
  if (!match?.[1]) return null;
  const parsed = Number.parseInt(match[1], 10);
  return Number.isFinite(parsed) ? parsed : null;
}

export function sortDocumentCandidates(candidates: DocumentCandidate[]): DocumentCandidate[] {
  return [...candidates].sort((a, b) => {
    const aChapter = chapterSortKey(a.display);
    const bChapter = chapterSortKey(b.display);

    if (aChapter != null && bChapter != null && aChapter !== bChapter) {
      return aChapter - bChapter;
    }
    if (aChapter != null && bChapter == null) return -1;
    if (aChapter == null && bChapter != null) return 1;

    return a.display.localeCompare(b.display, undefined, { numeric: true, sensitivity: "base" });
  });
}

/**
 * Titles that start with "Chapter" as a manuscript-style heading (Chapter 1, Chapter 00, CHAPTER IV, …).
 * We still index multi-word phrases ("chapter 1") and full titles; we skip the bare token `chapter` so
 * conversational "this chapter" does not collide with every chapter-titled document.
 */
export function isPatternedChapterManuscriptTitle(title: string): boolean {
  return /^\s*chapter\b/i.test(title.trim());
}

/**
 * Maps lowercased labels (full titles and non-trivial word tokens) to document candidates.
 * Aligns with client `buildLabelToMentionItemsMap` for documents in `autoEntityMentions.ts`.
 */
export function buildDocumentLabelMap(documents: { id: string; title: string }[]): Map<string, DocumentCandidate[]> {
  const map = new Map<string, DocumentCandidate[]>();
  const push = (label: string, item: DocumentCandidate) => {
    const key = label.trim().toLowerCase();
    if (!key) return;
    const existing = map.get(key) ?? [];
    if (existing.some((x) => x.id === item.id)) return;
    existing.push(item);
    map.set(key, existing);
  };
  const shouldIndexPhrase = (phrase: string): boolean => {
    const words = phrase.split(/\s+/).filter(Boolean);
    if (words.length === 0) return false;
    if (/\d/.test(phrase)) return true;
    return words.some((word) => isNotCommonWord(word));
  };

  for (const doc of documents) {
    const title = (doc.title || "").trim();
    if (!title) continue;
    const item: DocumentCandidate = { id: doc.id, display: title };
    push(title, item);
    const words = title.split(/\s+/).filter(Boolean);
    const skipSingleTokenSubtitleLabels = isPatternedChapterManuscriptTitle(title);
    for (const token of words) {
      if (!isNotCommonWord(token)) continue;
      const tokenKey = token.replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, "").toLowerCase();
      if (tokenKey === "chapter" && skipSingleTokenSubtitleLabels) {
        continue;
      }
      // Per-chapter files ("Chapter 27: The Shape of Yes") — index phrases, not lone subtitle
      // words like "yes" or "next" that collide with ordinary chat ("Yes, but…", "Next, there…").
      if (skipSingleTokenSubtitleLabels) continue;
      push(token, item);
    }
    // Index short multi-word phrases so "chapter 1" matches before generic "chapter".
    for (let start = 0; start < words.length; start++) {
      for (let length = 2; length <= 4 && start + length <= words.length; length++) {
        const phrase = words.slice(start, start + length).join(" ");
        if (shouldIndexPhrase(phrase)) {
          push(phrase, item);
        }
      }
    }
  }
  return map;
}

function sortedMatchLabelsFromMap(labelToItems: Map<string, DocumentCandidate[]>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const key of labelToItems.keys()) {
    const trimmed = key.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  out.sort((a, b) => b.length - a.length);
  return out;
}

/** Labels that map to more than one distinct document id. */
export function keysWithAmbiguousDocumentLabels(labelMap: Map<string, DocumentCandidate[]>): Set<string> {
  const ambiguous = new Set<string>();
  for (const [key, items] of labelMap) {
    const ids = new Set(items.map((i) => i.id));
    if (ids.size > 1) ambiguous.add(key.trim().toLowerCase());
  }
  return ambiguous;
}

/**
 * Non-overlapping matches in plain text where the label is ambiguous (multiple documents).
 * Longest labels first, same ordering as `findAutoMentionMatchesInPlainText` on the client.
 */
export function findAmbiguousDocumentMatchesInPlainText(
  text: string,
  labelMap: Map<string, DocumentCandidate[]>,
): Array<{ matchedText: string; start: number; end: number; candidates: DocumentCandidate[] }> {
  if (!text) return [];
  const labels = sortedMatchLabelsFromMap(labelMap);
  if (labels.length === 0) return [];

  const used = new Array(text.length).fill(false);
  /**
   * Positions covered by a multi-word label match or by a unique (single-doc) label — including
   * matches suppressed by trailing ?! — so single-token ambiguous labels cannot attach inside
   * that span (e.g. "chapter" inside "chapter 1?" or inside a resolved "chapter 00").
   */
  const phraseResolved = new Array(text.length).fill(false);
  const matches: Array<{ matchedText: string; start: number; end: number; candidates: DocumentCandidate[] }> = [];

  for (const label of labels) {
    const items = labelMap.get(label.toLowerCase());
    if (!items?.length) continue;
    const distinctIds = new Set(items.map((i) => i.id));
    const isAmbiguous = distinctIds.size > 1;
    const labelIsPhrase = /\s/.test(label.trim());

    const found = findEntityMatches(label, text);
    for (const { start, end } of found) {
      let overlap = false;
      for (let i = start; i < end; i++) {
        if (used[i]) {
          overlap = true;
          break;
        }
      }
      if (overlap) continue;

      const singleTokenAmbiguous = isAmbiguous && !labelIsPhrase;
      if (singleTokenAmbiguous) {
        let nestedInResolvedPhrase = true;
        for (let i = start; i < end; i++) {
          if (!phraseResolved[i]) {
            nestedInResolvedPhrase = false;
            break;
          }
        }
        if (nestedInResolvedPhrase) continue;
      }

      // Always mark positions as used — even if we skip this match — so that shorter
      // generic labels (e.g. bare "chapter") cannot fill in for "chapter 1?".
      for (let i = start; i < end; i++) used[i] = true;
      if (labelIsPhrase || !isAmbiguous) {
        for (let i = start; i < end; i++) phraseResolved[i] = true;
      }
      // A sentence-ending punctuation mark immediately after the match means the
      // user is not treating this as a document reference (e.g. "Chapter 1?").
      if (/[?!]/.test(text[end] ?? "")) continue;

      // Keep track of unique matches to block shorter generic overlaps (e.g. "chapter")
      // but only prompt the user for labels that are actually ambiguous.
      if (isAmbiguous) {
        matches.push({
          start,
          end,
          matchedText: text.slice(start, end),
          candidates: sortDocumentCandidates(items),
        });
      }
    }
  }

  matches.sort((a, b) => a.start - b.start);
  return matches;
}

/** Document ids referenced via #[label](document::id) or @[label](document::id). */
export function extractDocumentMentionIds(message: string): Set<string> {
  const ids = new Set<string>();
  const re = /(?:@|#)\[[^\]]*\]\(document::([^)]+)\)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(message)) !== null) {
    if (m[1]) ids.add(m[1]);
  }
  return ids;
}

/** Document ids referenced via #[label](series_document::bookProjectId::docId). */
export function extractSeriesDocumentMentionIds(message: string): Set<string> {
  const ids = new Set<string>();
  const re = /(?:@|#)\[[^\]]*\]\(series_document::[^:()]+::([^)]+)\)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(message)) !== null) {
    if (m[1]) ids.add(m[1]);
  }
  return ids;
}

/**
 * Uses prior user turns to infer the latest explicitly referenced manuscript document.
 * This lets follow-up messages like "that chapter" resolve to the active manuscript context.
 */
export function inferMostRecentMentionedDocumentId(
  history: Array<{ role?: string; content?: unknown }>,
): string | null {
  for (const msg of [...history].reverse()) {
    if (msg.role !== "user") continue;
    const content = typeof msg.content === "string" ? msg.content : "";
    if (!content.trim()) continue;

    const explicitDocumentIds = [
      ...extractSeriesDocumentMentionIds(content),
      ...extractDocumentMentionIds(content),
    ];
    if (explicitDocumentIds.length > 0) {
      return explicitDocumentIds[explicitDocumentIds.length - 1] ?? null;
    }
  }
  return null;
}

/**
 * Scans plain-text content of prior user messages to find the most recently referenced
 * document that unambiguously resolves to exactly one candidate. Used as a fallback when
 * no explicit #[](document::id) mention markup is found in history.
 *
 * Scans newest-first; within a message, longest labels win (same ordering as the
 * ambiguity checker) so "Chapter 2 Version A" shadows generic "Chapter 2".
 */
export function inferMostRecentPlainTextDocumentId(
  history: Array<{ role?: string; content?: unknown }>,
  documents: { id: string; title: string }[],
): string | null {
  if (documents.length === 0) return null;
  const labelMap = buildDocumentLabelMap(documents);
  if (labelMap.size === 0) return null;

  // Longest labels first so specific phrases shadow shorter generic ones
  const labels = [...labelMap.keys()].sort((a, b) => b.length - a.length);

  for (const msg of [...history].reverse()) {
    if (msg.role !== "user") continue;
    const content = typeof msg.content === "string" ? msg.content : "";
    if (!content.trim()) continue;

    // Explicit mention markup is authoritative
    const explicitIds = [
      ...extractSeriesDocumentMentionIds(content),
      ...extractDocumentMentionIds(content),
    ];
    if (explicitIds.length > 0) {
      return explicitIds[explicitIds.length - 1] ?? null;
    }

    // Scan plain-text segments outside mention markup for unambiguous matches
    for (const segment of splitMessagePlainTextSegments(content)) {
      if (!segment) continue;

      const used = new Array(segment.length).fill(false);
      for (const label of labels) {
        const items = labelMap.get(label.toLowerCase());
        if (!items?.length) continue;
        const distinctIds = new Set(items.map((i) => i.id));
        if (distinctIds.size !== 1) continue; // skip ambiguous labels

        const found = findEntityMatches(label, segment);
        for (const { start, end } of found) {
          let overlap = false;
          for (let i = start; i < end; i++) {
            if (used[i]) {
              overlap = true;
              break;
            }
          }
          if (overlap) continue;
          for (let i = start; i < end; i++) used[i] = true;
          if (/[?!]/.test(segment[end] ?? "")) continue;

          return [...distinctIds][0];
        }
      }
    }
  }

  return null;
}

/** Plain-text segments of the message (mention markup removed). */
export function splitMessagePlainTextSegments(message: string): string[] {
  return message.split(MENTION_MARKUP_RE);
}

/**
 * Removes echoed Q/A lines produced by the disambiguation UI itself:
 *   Q: Multiple documents match "..."
 *   A: ...
 * These lines are not fresh user intent and can retrigger ambiguity loops.
 */
export function stripDocumentClarificationEchoBlocks(message: string): string {
  const lines = message.split(/\r?\n/);
  const kept: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const next = lines[i + 1] ?? "";
    if (/^Q:\s*Multiple documents match\b/i.test(line.trim()) && /^A:/i.test(next.trim())) {
      i += 1;
      continue;
    }
    kept.push(line);
  }

  return kept.join("\n");
}

export type UnresolvedAmbiguousDocumentRef = {
  matchedText: string;
  candidates: DocumentCandidate[];
};

/**
 * Normalizes manuscript ids so mention ids like `doc-abc`, `doc-abc.md`,
 * or `/project/documents/doc-abc.md` can match raw `abc` ids.
 */
function normalizeDocumentIdForMatch(raw: string): string {
  const s = String(raw || "").trim();
  if (!s) return "";
  const pathMatch = s.match(/^\/project\/documents\/doc-([^/.]+)(?:\.md)?$/i);
  if (pathMatch) return pathMatch[1];
  const docPrefixMatch = s.match(/^doc-([^/.]+)(?:\.md)?$/i);
  if (docPrefixMatch) return docPrefixMatch[1];
  const bareMdMatch = s.match(/^([a-zA-Z0-9_-]+)\.md$/i);
  if (bareMdMatch) return bareMdMatch[1];
  return s;
}

function candidateSetKey(candidates: DocumentCandidate[]): string {
  return [...new Set(candidates.map((c) => c.id))].sort().join(",");
}

function isChapterLikeReference(text: string): boolean {
  const normalized = String(text || "").trim().toLowerCase();
  if (!normalized) return false;
  return /^chap(?:ter)?(?:\s+[\divxlcdm]+)?(?:\b|:)/i.test(normalized);
}

/**
 * Finds document references in **plain text** (outside `#[](...)` mentions) that match multiple
 * documents with the same label. Skips a match when the message already includes a `document::`
 * mention for one of those documents (user picked from the # menu).
 */
export function findUnresolvedAmbiguousDocumentReferences(
  message: string,
  documents: { id: string; title: string }[],
  opts?: { scopedDocumentIds?: string[] },
): UnresolvedAmbiguousDocumentRef[] {
  if (!message?.trim() || documents.length < 2) return [];
  const messageForMatching = stripDocumentClarificationEchoBlocks(message);

  const labelMap = buildDocumentLabelMap(documents);
  if (keysWithAmbiguousDocumentLabels(labelMap).size === 0) return [];

  const mentionedDocIds = extractDocumentMentionIds(message);
  const normalizedMentionedDocIds = new Set([...mentionedDocIds].map((id) => normalizeDocumentIdForMatch(id)));
  const normalizedScopedDocIds = new Set((opts?.scopedDocumentIds ?? []).map((id) => normalizeDocumentIdForMatch(id)).filter(Boolean));
  const seen = new Set<string>();
  const out: UnresolvedAmbiguousDocumentRef[] = [];

  for (const segment of splitMessagePlainTextSegments(messageForMatching)) {
    if (!segment) continue;
    const matches = findAmbiguousDocumentMatchesInPlainText(segment, labelMap);
    for (const m of matches) {
      const candidateIds = new Set(m.candidates.map((c) => c.id));
      const normalizedCandidateIds = new Set([...candidateIds].map((id) => normalizeDocumentIdForMatch(id)));
      const userDisambiguated = [...mentionedDocIds].some((id) => candidateIds.has(id))
        || [...normalizedMentionedDocIds].some((id) => normalizedCandidateIds.has(id));
      if (userDisambiguated) continue;
      // If the user already tagged a manuscript, treat generic chapter references as scoped to it.
      if (normalizedMentionedDocIds.size > 0 && isChapterLikeReference(m.matchedText)) continue;
      // If the request is already scoped to an active manuscript (e.g. editor documentId),
      // treat generic chapter references as scoped to that manuscript too.
      const requestScopedToCandidate = [...normalizedScopedDocIds].some((id) => normalizedCandidateIds.has(id));
      if (requestScopedToCandidate && isChapterLikeReference(m.matchedText)) continue;

      const dedupeKey = `${candidateSetKey(m.candidates)}|${m.matchedText.toLowerCase()}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      out.push({ matchedText: m.matchedText, candidates: m.candidates });
    }
  }

  return out;
}
